'use client'

export const SAFE_COW_APP_LINK = 'https://app.safe.global/share/safe-app?appUrl=https%3A%2F%2Fswap.cow.fi&chain=eth'
export const LINK_GUIDE_ADD_CUSTOM_TOKEN = 'https://cow.fi/learn/how-to-add-custom-tokens-on-cow-swap'
export const MY_ORDERS_ID = 'my-orders'

export const MEDIA_WIDTHS = {
  upToTiny: 320,
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
  upToLargeAlt: 1390,
  upToExtraLarge: 2560,
} as const

// TODO: Add proper return type annotation
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getMediaQuery = (query: string, useMediaPrefix = true) => {
  return useMediaPrefix ? `@media ${query}` : query
}

/**
 * Shared overlay stacking context (above dropdowns at 1000). Dialogs and drawers
 * use the same Layer z-index so a later portal — nested filters, receipts, etc. —
 * paints over the previous overlay.
 */
export const OVERLAY_Z_INDEX = {
  overlay: 1060,
} as const

export const INPUT_MIN_HEIGHT_PX = 45

export const Media = {
  upToTiny: (useMediaPrefix = true) => getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToTiny}px)`, useMediaPrefix),
  upToExtraSmall: (useMediaPrefix = true) =>
    getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToExtraSmall}px)`, useMediaPrefix),
  upToSmall: (useMediaPrefix = true) => getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToSmall}px)`, useMediaPrefix),
  MediumAndUp: (useMediaPrefix = true) => getMediaQuery(`(min-width: ${MEDIA_WIDTHS.upToSmall + 1}px)`, useMediaPrefix),
  isMediumOnly: (useMediaPrefix = true) =>
    getMediaQuery(
      `(min-width: ${MEDIA_WIDTHS.upToSmall + 1}px) and (max-width: ${MEDIA_WIDTHS.upToMedium}px)`,
      useMediaPrefix,
    ),
  upToMedium: (useMediaPrefix = true) => getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToMedium}px)`, useMediaPrefix),
  isLargeOnly: (useMediaPrefix = true) =>
    getMediaQuery(
      `(min-width: ${MEDIA_WIDTHS.upToMedium + 1}px) and (max-width: ${MEDIA_WIDTHS.upToLarge}px)`,
      useMediaPrefix,
    ),
  upToLarge: (useMediaPrefix = true) => getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToLarge}px)`, useMediaPrefix),
  upToLargeAlt: (useMediaPrefix = true) => getMediaQuery(`(max-width: ${MEDIA_WIDTHS.upToLargeAlt}px)`, useMediaPrefix),
  LargeAndUp: (useMediaPrefix = true) => getMediaQuery(`(min-width: ${MEDIA_WIDTHS.upToLarge + 1}px)`, useMediaPrefix),
}

export const Font = {
  familyInter: `'Inter var', 'Inter', Arial, sans-serif`,
  familyStudioFeixen: `'studiofeixen', 'Inter var', 'Inter', Arial, sans-serif`,
  familyStudioFeixenSerif: `'studiofeixenserif', Arial, serif`,
  // Numeric source of truth for ThemeColorVars and @font-face only. Use UI.FONT_WEIGHT_* in styles.
  weight: {
    ultralight: 200,
    light: 300,
    regular: 400,
    book: 450,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const

/**
 * Font size / line-height pairs in px.
 * Theme CSS vars and the `font()` mixin are derived from this map.
 */
export const FONT_SIZING = {
  FONT_SMALL: [12, 16],
  FONT_SMALL_PLUS: [13, 18],
  FONT_NORMAL: [14, 20],
  FONT_NORMAL_PLUS: [15, 20],
  FONT_MEDIUM: [16, 22],
  FONT_LARGE: [18, 24],
  FONT_LARGER: [20, 28],
} as const satisfies Record<string, readonly [fontSize: number, lineHeight: number]>

export type FontSizingName = keyof typeof FONT_SIZING
export type FontSizingPair = (typeof FONT_SIZING)[FontSizingName]
export type FontWeight = FontWeightValue | FontWeightKey
export type FontWeightKey = keyof typeof Font.weight
export type FontWeightValue = (typeof Font.weight)[FontWeightKey]

// Header offset in pixels (used in swap.cow.fi)
export const SWAP_HEADER_OFFSET = 76
